// src/components/products/components/DeleteModal.jsx

import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const DeleteModal = ({ isOpen, onClose, onConfirm, productName }) => {
  if (!isOpen) return null;
  
  const styles = {
    backdrop: {
      position: 'fixed',
      inset: 0,
      zIndex: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(0, 0, 0, 0.5)',
      padding: 'var(--spacing-m)'
    },
    modal: {
      background: 'var(--Surface-Primary)',
      borderRadius: 'var(--radius-m)',
      padding: 'var(--spacing-l)',
      maxWidth: '24rem',
      width: '100%',
      boxShadow: 'var(--shadow-2xl)',
      animation: 'modalFadeIn 0.2s ease',

      // Center content
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center'
    },
    iconWrapper: {
      width: '40px',
      height: '40px',
      borderRadius: 'var(--radius-xs)',
      background: 'var(--Surface-Error)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 'var(--spacing-s)'
    },
    title: {
      fontSize: 'var(--font-size-body2)',
      fontWeight: 'var(--font-weight-semibold)',
      color: 'var(--Text-Primary)',
      marginBottom: 'var(--spacing-xs)',
      fontFamily: 'var(--font-family-primary)'
    },
    description: {
      color: 'var(--Text-Secondary)',
      fontSize: 'var(--font-size-body3)',
      marginBottom: 'var(--spacing-l)',
      lineHeight: 'var(--line-height-normal)',
      fontFamily: 'var(--font-family-primary)'
    },
    productNameHighlight: {
      color: 'var(--Text-Primary)',
      fontWeight: 'var(--font-weight-medium)'
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
      gap: 'var(--spacing-s)',
      width: '100%'
    },
    button: {
      padding: 'var(--spacing-xs) var(--spacing-m)',
      borderRadius: 'var(--button-radius)',
      fontSize: 'var(--font-size-button)',
      fontWeight: 'var(--button-font-weight)',
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      fontFamily: 'var(--font-family-primary)'
    },
    cancelButton: {
      background: 'var(--Button-Secondary-Enabled-BG)',
      color: 'var(--Button-Secondary-Text)',
      border: '1px solid var(--Stroke-Light)'
    },
    deleteButton: {
      background: 'var(--Button-Delete-Enabled-BG)',
      color: 'var(--Button-Delete-Text)'
    }
  };

  // Add animation keyframes
  const styleSheet = `
    @keyframes modalFadeIn {
      from {
        opacity: 0;
        transform: scale(0.95);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    }
  `;
  
  return (
    <>
      <style>{styleSheet}</style>
      <div style={styles.backdrop} onClick={onClose}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div style={styles.iconWrapper}>
            <ExclamationTriangleIcon style={{ 
              width: '20px', 
              height: '20px', 
              color: 'var(--Red-600)' 
            }} />
          </div>
          
          <h3 style={styles.title}>Delete Product</h3>
          
          <p style={styles.description}>
            Are you sure you want to delete{' '}
            <span style={styles.productNameHighlight}>"{productName}"</span>? 
            This action cannot be undone.
          </p>
          
          <div style={styles.buttonContainer}>
            <button
              onClick={onClose}
              style={styles.button}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--Button-Secondary-Hovered-BG)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--Button-Secondary-Enabled-BG)';
              }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              style={{...styles.button, ...styles.deleteButton}}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--Button-Delete-Hovered-BG)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--Button-Delete-Enabled-BG)';
              }}
            >
              Delete Product
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default DeleteModal;